<script setup lang="ts">
import { useCategories } from '@/entities/categories/composables/useCategories';
import type { Category } from '@/entities/categories/model/types/categories';
import type { CategoriesList } from '@/widgets/categoriesList/model/types/categories-list';
import { PRODUCT_IMAGE_PLACEHOLDER_PATH } from '@/shared/constants/placeholders';

defineProps<CategoriesList>();

const { selectedItem } = useCategories();
const route = useRoute();
const { t, locale } = useI18n();
const localePath = useLocalePath();

const specialLinks: Record<string, string> = {
  promotions: localePath('promotions'),
  new: localePath('new'),
  sale: localePath('sale'),
  popular: localePath('popular'),
  liquidation: localePath('liquidation'),
};

const isCatalogDetail = computed(() =>
  route.name?.toString().includes('catalog-category'),
);

const getCategoryLink = (category: Category) => {
  const itemSlug = category[`slug_${locale.value}`];
  const isSpecialCategory = Object.keys(specialLinks).includes(String(category?.slug_en));

  if (!category.parent) {
    return isSpecialCategory
      ? specialLinks[category?.slug_en || '']
      : localePath({
          name: 'catalog-category',
          params: { category: itemSlug },
        });
  }
  return localePath({
    name: 'catalog-category-subcategory',
    params: { category: route.params.category, subcategory: itemSlug },
  });
};
</script>

<template>
  <div :class="['categories-list', { '_catalog-detail': isCatalogDetail }]">
    <div class="categories-list__title">{{ t('productCategories') }}</div>
    <ul class="categories-list__list">
      <li v-for="c in categories" :key="c.id" class="categories-list__item">
        <nuxt-link
          :to="getCategoryLink(c)"
          :class="['categories-list__link', { _active: c.id === selectedItem?.id }]"
          :aria-label="c.name"
        >
          <div
            v-if="isParent"
            class="categories-list__icon-wrapper"
            :style="{
              backgroundImage: `url(${c.icon || PRODUCT_IMAGE_PLACEHOLDER_PATH})`,
            }"
            :aria-label="c.name"
          ></div>
          {{ c.name }}
        </nuxt-link>
      </li>
    </ul>
  </div>
</template>

<style lang="less" scoped>
.categories-list {
  .flex-column(@size-x4);

  &__title {
    font-size: 14px;
    font-weight: 600;
    line-height: 130%;
    color: @color-dove-gray-secondary;
  }

  &__list {
    .flex-column(0px);
  }

  &__item {
    .flex-column(0px);

    border-bottom: 1px solid @color-alto;
  }

  &__link {
    .a-reset();

    display: flex;
    align-items: center;
    gap: @size-x3;
    font-size: @size-x4;
    line-height: 130%;
    font-weight: 500;
    color: @color-mine-shaft;
    padding: @size-x3 @size-x4;
    cursor: pointer;
    text-align: left;
    transition: background-color @A ease;

    &._active {
      font-weight: 600;
      line-height: 130%;
      color: #fff;
      background: @color-tango;

      &:hover {
        background: @color-tango;
      }
    }

    &:hover {
      background: @color-athens-gray-dark;
    }
  }

  &__icon-wrapper {
    width: @size-x10;
    height: @size-x10;
    background-size: cover;
    background-position: right;
    background-repeat: no-repeat;
    background-color: #fff;
    border-radius: @size-x2;
  }

  &._catalog-detail {
    margin: 0;
  }

  &._catalog-detail &__list {
    margin: 0 -@size-x4;
  }

  &._catalog-detail &__item {
    border-bottom: none;
  }

  @media @1024 {
    &__link {
      font-size: 15px;
    }
  }
}
</style>
